type AccountMinimumItemParams = {
  wrapper: string;
  value: string;
  currency: string;
  minimum: string;
  shortfall: string;
};

export const T = {
  en_GB: {
    loadingCustomPortfolio: "Loading custom portfolio...",
    noProductsAddedYet: "No products added yet",
    noProductsWereAdded: "No products were added to this portfolio.",
    buildYourCustomPortfolio:
      "Build your custom portfolio by adding products from the product register.",
    addFirstProduct: "Add First Product",
    // QL-375 group 6 — screen 10: the mandate entry point in the empty state.
    buildYourCustomPortfolioOrMandate:
      "Build your custom portfolio by adding products from the product register, or create it from a mandate template.",
    applyMandate: "Create from mandate",
    // QL-677 — no active template on the goal's platform to build from.
    applyMandateNoTemplates: "No mandate template is available on the goal's platform.",
    addProduct: "Add Product",
    unitsMustBeWholeNumber: "Units must be a whole number of at least 1",
    cancel: "Cancel",
    deleteProduct: "Delete Product",
    deleteProductConfirmation:
      "Are you sure you want to delete this product? The weights for remaining products will be automatically recalculated.",
    delete: "Delete",
    error: "Error",
    validationError: "Validation Error",
    amountMustBeGreaterThanZero: "Amount must be greater than 0",
    pleaseSelectProduct: "Please select a product",
    productUpdatedSuccessfully: "Saved",
    productAddedSuccessfully: "Product added",
    failedToSaveProduct: "Failed to save product",
    productDeletedSuccessfully: "Product deleted successfully",
    failedToDeleteProduct: "Failed to delete product",
    incompleteMessage: "Add at least one product to the custom portfolio",
    minBuyViolationsMessage:
      "One or more products are below their minimum buy amount. Increase the amounts or remove the products.",
    accountMinimumBlockedMessage:
      "An account this portfolio invests into would hold less than the minimum for its wrapper type. Invest more into it or sell less from it.",
    accountMinimumItem: (p: AccountMinimumItemParams) =>
      `${p.wrapper} account would hold ${p.value} ${p.currency}, below the ${p.minimum} ${p.currency} minimum for this wrapper type (short by ${p.shortfall} ${p.currency}).`,
    switchOverAllocatedMessage:
      "A holding's switch instructions exceed what the client holds. Reduce the switch pairs on it.",
    switchRegimeConflictMessage:
      "A holding has both switch instructions and a buy/sell change. Remove one of them — a holding can only carry one kind of instruction.",
    switchWrapperIncompatibleMessage:
      "A switch target can no longer be held by the source account (its wrapper type changed). Remove the switch pair — to move into that product, sell and buy it with new money instead.",
    minBuyDialogError: (min: string) => `Amount is below the minimum buy of ${min}`,
    notAvailableForPurchase:
      "This product is not available for new purchases. You can keep or reduce the current holding.",
    // Review #3 — neutral on purpose: this also fires for an agreement
    // follow-up's currently funded savings-plan line, where "nothing of it
    // is held yet" would be a materially wrong claim about money the client
    // has actually invested.
    lockedRemoveOnly:
      "This product can no longer be purchased in this flow. It can only be removed from the portfolio.",
    lockedHoldingsBannerFollowup:
      "Some holdings can no longer be purchased (the product is inactive, outside the flow's product collection, or not available under the goal's platform). Locked rows are marked with a lock icon and can only be kept or reduced.",
    lockedHoldingsBannerRemove:
      "Some holdings can no longer be purchased (the product is inactive, outside the flow's product collection, or not available under the goal's platform). Locked rows are marked with a lock icon and must be removed.",
    lockedHoldingsBannerMixed:
      "Some holdings can no longer be purchased (the product is inactive, outside the flow's product collection, or not available under the goal's platform). Locked rows are marked with a lock icon: rows with an existing holding can only be kept or reduced, the others must be removed.",
    followupDisclaimer:
      "Some instruments in this portfolio are not part of the current product collection. Certain analytics (forecast, expected value) may be unavailable.",
    failedToUpdateHolding: "Failed to update the holding",
    failedToUpdateProduct: "Failed to update the product",
    minBuyTopupError: (buy: string, min: string) =>
      `The purchase of ${buy} is below the minimum buy of ${min}.`,
    minBuyFollowupViolationsMessage:
      "A purchase or switch is below the fund's minimum buy. Increase it, remove it, or keep the holding unchanged.",
  },
  nb_NO: {
    loadingCustomPortfolio: "Laster tilpasset portefølje...",
    noProductsAddedYet: "Ingen produkter lagt til ennå",
    noProductsWereAdded: "Ingen produkter ble lagt til i denne porteføljen.",
    buildYourCustomPortfolio:
      "Bygg din tilpassede portefølje ved å legge til produkter fra produktregisteret.",
    addFirstProduct: "Legg til første produkt",
    buildYourCustomPortfolioOrMandate:
      "Bygg din egendefinerte portefølje ved å legge til produkter fra produktregisteret, eller opprett den fra en mandatmal.",
    applyMandate: "Opprett fra mandat",
    applyMandateNoTemplates: "Ingen mandatmal er tilgjengelig på målets plattform.",
    addProduct: "Legg til produkt",
    unitsMustBeWholeNumber: "Andeler må være et helt tall på minst 1",
    cancel: "Avbryt",
    deleteProduct: "Slett produkt",
    deleteProductConfirmation:
      "Er du sikker på at du vil slette dette produktet? Vektene for gjenværende produkter vil bli automatisk omberegnet.",
    delete: "Slett",
    error: "Feil",
    validationError: "Valideringsfeil",
    amountMustBeGreaterThanZero: "Beløp må være større enn 0",
    pleaseSelectProduct: "Vennligst velg et produkt",
    productUpdatedSuccessfully: "Lagret",
    productAddedSuccessfully: "Produkt lagt til",
    failedToSaveProduct: "Kunne ikke lagre produkt",
    productDeletedSuccessfully: "Produkt slettet",
    failedToDeleteProduct: "Kunne ikke slette produkt",
    incompleteMessage: "Legg til minst ett produkt i den tilpassede porteføljen",
    minBuyViolationsMessage:
      "Ett eller flere produkter er under sitt minimumsbeløp for kjøp. Øk beløpene eller fjern produktene.",
    accountMinimumBlockedMessage:
      "En konto denne porteføljen investerer i ville hatt mindre enn minimumsverdien for sin innpakningstype. Invester mer i den eller selg mindre fra den.",
    accountMinimumItem: (p: AccountMinimumItemParams) =>
      `${p.wrapper}-kontoen ville hatt ${p.value} ${p.currency}, under minimumsverdien på ${p.minimum} ${p.currency} for denne innpakningstypen (mangler ${p.shortfall} ${p.currency}).`,
    switchOverAllocatedMessage:
      "Bytteinstruksjonene for en beholdning overstiger det kunden eier. Reduser bytteparene på den.",
    switchRegimeConflictMessage:
      "En beholdning har både bytteinstruksjoner og en kjøps-/salgsendring. Fjern én av dem — en beholdning kan bare ha én type instruksjon.",
    switchWrapperIncompatibleMessage:
      "Et byttemål kan ikke lenger holdes av kildekontoen (kontoens innpakningstype er endret). Fjern bytteparet — selg og kjøp produktet med nye midler i stedet.",
    minBuyDialogError: (min: string) => `Beløpet er under minimumskjøpet på ${min}`,
    notAvailableForPurchase:
      "Dette produktet er ikke tilgjengelig for nye kjøp. Du kan beholde eller redusere gjeldende beholdning.",
    lockedRemoveOnly:
      "Dette produktet kan ikke lenger kjøpes i denne flyten. Det kan bare fjernes fra porteføljen.",
    lockedHoldingsBannerFollowup:
      "Noen beholdninger kan ikke lenger kjøpes (produktet er inaktivt, utenfor flytens produktsamling eller ikke tilgjengelig på målets plattform). Låste rader er merket med en hengelås og kan bare beholdes eller reduseres.",
    lockedHoldingsBannerRemove:
      "Noen beholdninger kan ikke lenger kjøpes (produktet er inaktivt, utenfor flytens produktsamling eller ikke tilgjengelig på målets plattform). Låste rader er merket med en hengelås og må fjernes.",
    lockedHoldingsBannerMixed:
      "Noen beholdninger kan ikke lenger kjøpes (produktet er inaktivt, utenfor flytens produktsamling eller ikke tilgjengelig på målets plattform). Låste rader er merket med en hengelås: rader med en eksisterende beholdning kan bare beholdes eller reduseres, de andre må fjernes.",
    followupDisclaimer:
      "Noen instrumenter i denne porteføljen er ikke en del av den nåværende produktsamlingen. Visse analyser (prognose, forventet verdi) kan være utilgjengelige.",
    failedToUpdateHolding: "Kunne ikke oppdatere beholdningen",
    failedToUpdateProduct: "Kunne ikke oppdatere produktet",
    minBuyTopupError: (buy: string, min: string) =>
      `Kjøpet på ${buy} er under minstekjøpet på ${min}.`,
    minBuyFollowupViolationsMessage:
      "Et kjøp eller bytte er under fondets minstekjøp. Øk det, fjern det, eller behold beholdningen uendret.",
  },
} as const;
